import React, { useState, useEffect } from "react";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom"

const AdditionalNotes = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data, schoolProfileId, onUpdate, schoolProfile } = props; 
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loader, setLoader] = useState(false);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    student_issues: "",
    remarks: "",
    tree_plantation: true, // Default value for tree plantation as boolean
  });

  // ✅ Check if Functional
  const isFunctional =
    schoolProfile?.status_of_school?.toLowerCase() === "functional";


    const fetchSchoolAssessment = async () => {
        try {
          setLoader(true);
          setIsFetching(true);
    
          const response = await fetchData(
            `school-fetchById/${id}`,
            "GET",
            null,
            {},
            {},
            navigate
          );
    
          if (response?.data) {
            const isDqaApproved =
              response.data?.SchoolProfile.verified_from_dqa === 1 ||
              response.data?.SchoolProfile.verified_from_dqa === "1";
    
              const isDqaRejected = 
              response.data?.SchoolProfile.verified_from_dqa === 2 ||
              response.data?.SchoolProfile.verified_from_dqa === "2";
    
            setIsDqaApproved(isDqaApproved);
            setIsDqaRejected(isDqaRejected)  
          }
        } catch (error) {
          console.error("Error fetching school data:", error);
          toast.error("Failed to load school data.");
        } finally {
          setLoader(false);
          setIsFetching(false);
        }
      };
    
      useEffect(() => {
        fetchSchoolAssessment();
      }, [id]);

  // ✅ Dynamic Yup validation schema
  const validationSchema = Yup.object().shape({
    student_issues: Yup.string().when([], {
      is: () => isFunctional,
      then: (schema) =>
        schema
          .required("Student issues field is required")
          .max(500, "Student issues cannot exceed 500 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    remarks: Yup.string()
      .required("Remarks field is required")
      .max(300, "Remarks cannot exceed 300 characters"),
    tree_plantation: Yup.boolean()
      .oneOf([true, false], "Tree plantation status is required")
      .required("Tree plantation status is required"),
  });

  useEffect(() => {
    if (data) {
      setFormData({
        student_issues: data.student_issues || "",
        remarks: data.remarks || "",
        tree_plantation: data.tree_plantation || false, // Correctly setting the backend value
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setErrors({});
    try {
      // ✅ Validate dynamically
      await validationSchema.validate(formData, { abortEarly: false });

      const profileId = schoolProfileId || data?.school_profile_id;
      if (!profileId) {
        toast.error("School profile ID not found");
        return;
      }

      const updatePayload = {
        student_issues: isFunctional ? formData.student_issues : "", // ✅ clear issues if not functional
        remarks: formData.remarks,
        tree_plantation: formData.tree_plantation, // Send tree plantation data as boolean
      };

      const response = await fetchData(
        `school-combined-update/${profileId}`,
        "PUT",
        {
          school_additional_notes: JSON.stringify(updatePayload),
          school_profile: "{}",
          school_infrastructure: "{}",
          school_learning_facilities: "{}",
          school_administrative_details: "{}",
        },
        { "Content-Type": "application/json" }
      );

      if (response.success) {
        toast.success("Additional notes updated successfully!");
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.message || "Failed to update additional notes");
      }
    } catch (error) {
      if (error.inner) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        toast.error("Error updating additional notes");
        console.error("Update error:", error);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Additional Notes
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Additional Notes
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          className={`md:col-span-2 grid ${
            isFunctional ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
          } gap-6`}
        >
          {/*Show Issues only if Functional */}
          {isFunctional && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issues faced by students *
              </label>
              <textarea disabled={isDqaApproved || isDqaRejected}
                rows={4}
                className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={formData.student_issues}
                onChange={(e) => handleChange("student_issues", e.target.value)}
                placeholder="Write issues here..."
              />
              {errors.student_issues && (
                <p className="text-red-500 text-xs">{errors.student_issues}</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isFunctional ? "Remarks" : "Remarks *"}
            </label>
            <textarea disabled={isDqaApproved || isDqaRejected}
              rows={4}
              className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={formData.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
              placeholder="Write remarks here..."
            />
            {errors.remarks && (
              <p className="text-red-500 text-xs">{errors.remarks}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              3. Tree Plantation *
            </label>
            <RadioGroup
              value={formData.tree_plantation.toString()}
              onChange={(e) =>
                handleChange("tree_plantation", e.target.value === "true")
              }
            >
              <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="true" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="Yes" />
              <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="false" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="No" />
            </RadioGroup>
            {errors.tree_plantation && (
              <p className="text-red-500 text-xs">{errors.tree_plantation}</p>
            )}
          </div>
        </div>
      </div>
      {!isDqaApproved && !isDqaRejected ? (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`${
              isUpdating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } text-white px-5 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      ) : (
        <div className=""></div>
      )}
    </>
  );
};

export default AdditionalNotes;
